import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-search-css.js';
import '@zywave/zui-icons';
import type { ZuiSelectElement } from '@zywave/zui-select';

/**
 * Standardized component for a search input.
 * @element zui-search
 *
 * @event {CustomEvent} search - Event is dispatched when a search is performed; provides details of input value, search type, and filter discriminator value from dropdown (if given)
 *
 * @slot - Default, unnamed slot; for inserting and placing a `<zui-select>` or `<select>` element next to the search field
 *
 * @cssprop [--zui-search-placeholder-color=var(--zui-gray-300)] - Color of placeholder text
 * @cssprop [--zui-search-submit-background-color=var(--zui-gray-50)] - Background color on submit button
 * @cssprop [--zui-search-submit-hover-background-color=var(--zui-gray-100)] - Background hover color on submit button
 * @cssprop [--zui-search-submit-color=var(--zui-blue)] - Change the color of search icon
 * @cssprop [--zui-search-submit-hover-color=var(--zui-blue)] - Change the hover color of search icon
 * @cssprop [--zui-select-border-radius=0 4px 4px 0] - Use to change border radius
 * @cssprop [--zui-search-border-color=var(--zui-gray-200)] - Border color on slotted select element
 *
 * @attr {string | null} [name=null] - The name of this element that is associated with form submission
 * @attr {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @attr {boolean} [readonly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @attr {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @attr {string} [value=''] - Represents the value of the search input box. Can be set to a default value, and will reflect the value provided by the user when interactive with the control.
 *
 * @prop {string | null} [name=null] - The name of this element that is associated with form submission
 * @prop {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @prop {boolean} [readOnly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @prop {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @prop {string} [value=''] - Represents the value of the search input box. Can be set to a default value, and will reflect the value provided by the user when interactive with the control.
 */
export class ZuiSearch extends ZuiFormAssociatedElement {
  protected get _focusControlSelector() {
    return 'input';
  }

  protected get _formValue() {
    return this.value;
  }

  protected formResetCallback() {
    this.value = '';
  }

  /**
   * Default placeholder (ghost) text
   */
  @property({ type: String, attribute: 'placeholder' })
  placeholder: string | undefined = undefined;

  @property()
  get value() {
    return this.#value;
  }

  set value(val) {
    const oldVal = this.#value;
    this.#value = val;
    this._setFormValue(this.#value);
    this.requestUpdate('value', oldVal);
  }

  /**
   * Hide the submit button
   */
  @property({ type: Boolean, attribute: 'no-submit' })
  noSubmit = false;

  @query('input')
  _inputEl: HTMLInputElement;

  @query('button')
  _buttonEl: HTMLButtonElement;

  #value = '';

  _deferFocus = true;
  _deferClick = true;

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();

    // we want to go a little faster than LitElement and behave more like native HTML Form Associated Elements
    const value = this.value ?? this.getAttribute('value');
    this._setFormValue(value);
  }

  render() {
    const title = this.title ?? '';
    return html`
      <section class="wrapper">
        <zui-icon icon="zui-search" class="search-icon ${classMap({ 'no-submit': this.noSubmit })}"></zui-icon>
        <div>
          <input
            aria-label="Search"
            type="search"
            .value="${this.value || ''}"
            placeholder="${this.placeholder || ''}"
            ?disabled="${this.disabled}"
            @input="${this.updateValue}"
            @keyup="${this._handleKeyEvent}"
            title="${title}"
          />
        </div>
        <slot></slot>
        <button
          class="submit ${classMap({ 'no-submit': this.noSubmit })}"
          @click="${this.search}"
          ?disabled="${this.disabled}"
          ><zui-icon icon="zui-search"></zui-icon
        ></button>
      </section>
    `;
  }

  /**
   * Use to trigger a search, imperatively clicks the search's input
   */
  click(): void {
    if (this._inputEl) {
      this._inputEl.click();
    }
  }

  /**
   * Use to direct focus onto the search's input
   */
  focus(): void {
    if (this._inputEl) {
      this._inputEl.focus();
    }
  }

  private search(event: Event) {
    const searchType: ZuiSelectElement = this.querySelector('zui-select');
    let selectedFilterValue = null;
    if (searchType) {
      selectedFilterValue = searchType.value;
    }
    this.dispatchEvent(
      new CustomEvent('search', {
        bubbles: true,
        composed: true,
        detail: {
          value: this.value,
          searchType: selectedFilterValue,
        },
      })
    );
    // pressing enter within the zui-search element will already submit the form
    // this is to explicitly cover clicking the button itself
    if (event instanceof MouseEvent) {
      this._submitForm();
    }
  }

  private updateValue(e: InputEvent) {
    this.value = (e.target as HTMLInputElement).value;
  }

  private _submitForm() {
    if (!this.form) {
      return;
    }
    if (this.form.requestSubmit) {
      this.form.requestSubmit(this._formSubmitButton);
    } else {
      let btn = null;
      if (this._formSubmitButton) {
        btn = this._formSubmitButton;
      } else {
        btn = document.createElement('button');
        btn.type = 'submit';
        btn.style.visibility = 'hidden';
        btn.style.width = '0';
        btn.style.height = '0';
        this.form.appendChild(btn);
      }
      if (btn) {
        btn.click();
      }
    }
  }

  private _handleKeyEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.search(event);
    }
  }
}

window.customElements.define('zui-search', ZuiSearch);

declare global {
  interface HTMLElementTagNameMap {
    'zui-search': ZuiSearch;
  }
}
